/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useCallback, useState } from "react";
import { useUserStore } from "../stores/useUserStore";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api.config";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const navigate = useNavigate();
  const {
    user,
    token,
    role,
    companyId,
    userId,
    isAuthenticated,
    login,
    logout,
    updateUser,
    replaceUser,
  } = useUserStore();

  const [authReady, setAuthReady] = useState(() => Boolean(isAuthenticated && user));
  const activeUserId = user?.id || null;
  const activeCompanyId = user?.company_id || null;

  const handleLogout = useCallback(async () => {
    try {
      await fetch(buildApiUrl('api/auth.php'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logout' }),
      });
    } catch {
      // ignore network errors — clear locally regardless
    }
    logout();
    navigate("/login");
  }, [logout, navigate]);

  // Reconcile the durable server session with the persisted local identity.
  // A network failure does not sign an installed PWA out; only an explicit
  // server response saying the session is gone clears local authentication.
  useEffect(() => {
    // Public visitors do not need an authentication round trip. Only restore a
    // server session when a durable local identity already exists; protected
    // routes still wait for authReady before rendering.
    if (!isAuthenticated || !activeUserId) {
      setAuthReady(true);
      return undefined;
    }

    const controller = new AbortController();

    fetch(buildApiUrl("api/auth.php?action=check"), {
      credentials: "include",
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!payload) throw new Error("Session endpoint did not return JSON");

        if (response.ok && payload.logged_in && payload.user) {
          login({ user: payload.user });
        } else if (response.ok && payload.logged_in === false) {
          logout();
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.warn("Session refresh unavailable; preserving the saved login.", error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setAuthReady(true);
      });

    return () => controller.abort();
  // Depend on stable identity primitives. Depending on the entire user object
  // retriggered this request after every session payload replacement and could
  // make the dashboard appear to blink in a refresh loop.
  }, [activeCompanyId, activeUserId, isAuthenticated, login, logout]);

  // Check if user session is still valid
  useEffect(() => {
    if (isAuthenticated && !user) {
      // Session expired or corrupted
      handleLogout();
    }
  }, [isAuthenticated, user, handleLogout]);

  const handleLogin = (userData) => {
    login(userData);
    setAuthReady(true);
  };

  const value = {
    user,
    token,
    role,
    companyId,
    userId,
    isAuthenticated,
    authReady,
    login: handleLogin,
    logout: handleLogout,
    updateUser,
    replaceUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use user context
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
